/**
 * Client-side cryptographic utilities for agent key generation
 * Uses Web Crypto API for Ed25519 key generation
 */

/**
 * Generate Ed25519 key pair in browser
 * @param {string} agentId - The agent ID to use as the key ID (kid)
 * @returns {Promise<{publicJWK: Object, privateKey: string, keyId: string, publicKey: CryptoKey, thumbprint: string}>}
 */
export async function generateAgentKeys(agentId) {
  try {
    // Generate Ed25519 key pair using Web Crypto API
    const keyPair = await crypto.subtle.generateKey(
      {
        name: 'Ed25519'
      },
      true, // extractable
      ['sign', 'verify']
    );

    // Export public key as JWK
    const rawJWK = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
    
    // Create normalized JWK with only required fields
    const publicJWK = {
      kty: 'OKP',
      crv: 'Ed25519',
      kid: agentId,
      x: rawJWK.x
    };

    // Export private key as PKCS8 for compatibility
    const privateKeyBuffer = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
    const privateKeyPem = bufferToPem(privateKeyBuffer, 'PRIVATE KEY');

    // Generate thumbprint
    const thumbprint = await generateJWKThumbprint(publicJWK);

    // Generate self-signature for HTTP Message Signatures Directory
    const directorySignature = await generateDirectorySelfSignature(
      keyPair.privateKey,
      publicJWK,
      thumbprint,
      agentId
    );

    return {
      publicJWK,
      privateKey: privateKeyPem,
      keyId: agentId,
      publicKey: keyPair.publicKey, // Keep for signing operations
      thumbprint,
      directorySignature
    };
  } catch (error) {
    throw new Error(`Key generation failed: ${error.message}`);
  }
}

/**
 * Generate HTTP Message Signatures Directory self-signature
 * Implements draft-meunier-http-message-signatures-directory-01
 * @param {CryptoKey} privateKey - The private key for signing
 * @param {Object} publicJWK - The public key JWK
 * @param {string} thumbprint - JWK thumbprint
 * @param {string} agentId - Agent ID
 * @returns {Promise<Object>} Signature data with signatureInput and signature
 */
async function generateDirectorySelfSignature(privateKey, publicJWK, thumbprint, agentId) {
  try {
    const now = Math.floor(Date.now() / 1000);
    const expires = now + (180 * 24 * 60 * 60); // 180 days from now
    
    // Construct the authority component (@authority)
    const authority = window.location.host;
    
    // Construct signature parameters (order matters for RFC 9421 compliance)
    const signatureParams = [
      `created=${now}`,
      `keyid="${thumbprint}"`,
      `alg="ed25519"`,
      `expires=${expires}`,
      `tag="http-message-signatures-directory"`
    ].join(';');
    
    // Construct signature input (only @authority component per RFC 9421)
    const signatureInput = `sig1=("@authority");${signatureParams}`;
    
    // Create signature base string (only @authority component)
    const encoder = new TextEncoder();
    const signatureBase = [
      `"@authority": ${authority}`,
      `"@signature-params": ("@authority");${signatureParams}`
    ].join('\n');
    
    // Sign the signature base
    const signatureBytes = encoder.encode(signatureBase);
    const signatureBuffer = await crypto.subtle.sign('Ed25519', privateKey, signatureBytes);
    const signature = `sig1=:${arrayBufferToBase64(signatureBuffer)}:`;
    
    return {
      signatureInput,
      signature,
      signatureExpires: expires,
      authority
    };
  } catch (error) {
    console.error('Directory signature generation failed:', error);
    throw new Error(`Directory signature generation failed: ${error.message}`);
  }
}

/**
 * Generate JWK thumbprint according to RFC 7638
 * @param {Object} jwk - The JWK to generate a thumbprint for
 * @returns {Promise<string>} Base64URL-encoded thumbprint
 */
async function generateJWKThumbprint(jwk) {
  // Create canonical JWK by including only required fields in lexicographic order
  const canonical = JSON.stringify({
    crv: jwk.crv,
    kty: jwk.kty,
    x: jwk.x
  });
  
  // Generate SHA-256 hash
  const encoder = new TextEncoder();
  const data = encoder.encode(canonical);
  const hash = await crypto.subtle.digest('SHA-256', data);
  
  // Convert to base64url
  return arrayBufferToBase64Url(hash);
}

/**
 * Convert an ArrayBuffer to base64url format
 * @param {ArrayBuffer} buffer - The buffer to convert
 * @returns {string} Base64URL-encoded string
 */
function arrayBufferToBase64Url(buffer) {
  // Convert ArrayBuffer to base64
  const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
  
  // Convert base64 to base64url
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Convert an ArrayBuffer to standard base64 format (for HTTP Message Signatures)
 * @param {ArrayBuffer} buffer - The buffer to convert
 * @returns {string} Base64-encoded string
 */
function arrayBufferToBase64(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

/**
 * Convert ArrayBuffer to PEM format
 * @param {ArrayBuffer} buffer - The buffer to convert
 * @param {string} type - The PEM type (e.g., 'PRIVATE KEY', 'PUBLIC KEY')
 * @returns {string} PEM-formatted string
 */
function bufferToPem(buffer, type) {
  // Convert buffer to base64
  const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
  
  // Split into 64-character lines
  const lines = base64.match(/.{1,64}/g) || [];
  
  // Build PEM format
  return [
    `-----BEGIN ${type}-----`,
    ...lines,
    `-----END ${type}-----`
  ].join('\n');
}

/**
 * Check if the browser supports Ed25519 key generation
 * @returns {Promise<boolean>} True if supported, false otherwise
 */
export async function isEd25519Supported() {
  try {
    // Try to generate a test key pair
    await crypto.subtle.generateKey(
      {
        name: 'Ed25519'
      },
      true,
      ['sign', 'verify']
    );
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Get minimum browser versions that support Ed25519
 * @returns {Object} Object containing browser version requirements
 */
export function getEd25519BrowserRequirements() {
  return {
    chrome: '85',
    firefox: '94',
    safari: '16.4',
    edge: '85'
  };
} 